// Dependencies
import React from 'react';
import { from, Subject } from 'rxjs';
import { exhaustMap, delay } from 'rxjs/operators';

// Hooks
import { useIsMounted } from 'usehooks-ts';
import { useTranslation, Trans } from 'next-i18next';

// API
import api from 'src/api';

// Material UI Components
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import CircularProgress from '@mui/material/CircularProgress';
// Internal Components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';

// External Components
import NextLink from 'next/link';
import dayjs from 'dayjs';
import { useSelector } from 'src/store/store';
import { NumericFormat } from 'react-number-format';
import separators from 'src/utils/separators';
import { useSocket } from 'src/socket/socketContext';

// Types
type Notification = {
  id: string;
  category: typeof categories[number];
  templateId: string;
  templateData: Record<string, any> | null;
  lockedUntil: Date | null;
  readAt: Date | null;
  createdAt: Date;
};

// ----------------------------------------------------------------------
/**
 * Notifications categories
 */
const categories = ['wallet', 'security', 'trading', 'affiliates'] as const;

const notificationsUpdateSubject = new Subject<boolean | undefined>();

/**
 * Notifications popover component
 */
export default function NotificationsPopover() {
  const { socket } = useSocket();
  // =={ Redux }==============================================================
  const { isAuthorization, required: authRequiredOp } = useSelector((state) => state.auth);

  // =={ States }==============================================================
  /**
   * Popover state
   */
  const [open, setOpen] = React.useState<HTMLElement | null>(null);

  /**
   * Display filter state
   */
  const [category, setCategory] = React.useState<string>('');

  /**
   * Notifications state
   */
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [unreadCounts, setUnreadCounts] = React.useState<Record<string, number>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // =={ Refs }==============================================================
  const refetchIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // =={ Memos }==============================================================
  const allCount = React.useMemo(
    () => Object.values(unreadCounts).reduce((acc, count) => +acc + +count, 0),
    [unreadCounts]
  );

  const lockedCount = React.useMemo(
    () => notifications.filter((n) => n.lockedUntil && new Date(n.lockedUntil) > new Date()).length,
    [notifications]
  );

  const isAuthToFetch = React.useMemo(
    () => isAuthorization && authRequiredOp === null,
    [isAuthorization, authRequiredOp]
  );

  // =={ Hooks }==============================================================
  const { t } = useTranslation('notifications');
  const isMounted = useIsMounted();

  // =={ Handlers }==============================================================
  /**
   * Handle opening the popover
   */
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCategory('');
    setOpen(event.currentTarget);
  };

  /**
   * Handle closing the popover
   */
  const handleClose = () => {
    setOpen(null);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleMarkAsRead = (notificationId: string) => {
    let foundNotification: Notification | null = null;

    setNotifications((prev) =>
      prev.map((notification) => {
        if (notification.id === notificationId) {
          foundNotification = notification;
          return {
            ...notification,
            readAt: new Date(),
          };
        }
        return notification;
      })
    );

    // Update unread counts
    setUnreadCounts((prev) => {
      if (foundNotification) {
        const countsCopy = { ...prev };
        countsCopy[foundNotification.category] = (countsCopy[foundNotification.category] || 0) - 1;
        return countsCopy;
      }
      return prev;
    });

    markAsRead(notificationId);
  };

  /**
   * Handle marking all notifications as read
   */
  const handleMarkAllAsRead = () => {
    const lockedCounts: Record<string, number> = {};

    // Set all notifications as read
    setNotifications((prev) =>
      prev.map((notification) => {
        if (notification.lockedUntil && new Date(notification.lockedUntil) > new Date()) {
          lockedCounts[notification.category] = (lockedCounts[notification.category] || 0) + 1;
          return notification; // Skip locked notifications
        }
        return {
          ...notification,
          readAt: new Date(),
        };
      })
    );

    // Reset unread counts
    setUnreadCounts((prev) => {
      const countsCopy = { ...prev };
      categories.forEach((category) => {
        countsCopy[category] = 0;
      });

      return { ...countsCopy, ...lockedCounts };
    });

    // Mark all as read in the backend
    markAllAsRead();
  };

  /**
   * Handle changing the category
   */
  const handleChangeCategory = (event: React.SyntheticEvent, newValue: string) => {
    setCategory(newValue);
  };

  const fetchNotifications = React.useCallback(
    async (ignoreErrors = false) => {
      // Check if components still mounted
      if (!isMounted()) return;

      if (!isAuthToFetch) return;

      if (!ignoreErrors) {
        // Set loading state
        setLoading(true);

        // Reset error state
        setError('');
      }

      // Define error
      let error = '';

      // Fetch
      try {
        // Get data
        const { data } = await api.get<{
          success: boolean;
          data: {
            rows: Notification[];
            counts: {
              [key in typeof categories[number]]: number;
            };
          };
        }>('/notifications', {
          params: {
            ...(!category
              ? {}
              : category === 'unread'
              ? { isRead: false }
              : {
                  category,
                }),
          },
        });

        if (!data.success) throw new Error('UNKNOWN_ERROR');

        // Check if component still mounted
        if (!isMounted()) return;

        // Set notifications
        setNotifications(data.data.rows);
        setUnreadCounts(data.data.counts);
      } catch (e) {
        error = e.message;
      } finally {
        // Check if component still mounted
        if (!isMounted()) return;
        if (!ignoreErrors) {
          setLoading(false);
          setError(error);
        }
      }
    },
    [category, isMounted, isAuthToFetch]
  );

  // =={ Effects }==============================================================
  /**
   * Fetch notifications on mount and set up refetch interval
   */
  React.useEffect(() => {
    /**
     * Throttle notifications updates to avoid excessive API calls.
     */
    const throttledNotificationsUpdates$ = notificationsUpdateSubject.pipe(
      exhaustMap(
        (value) => from(fetchNotifications(value)).pipe(delay(3000)) // Delay the next fetch by 3 seconds
      )
    );

    /**
     * Subscribe to the throttled notifications updates stream.
     */
    const subscription = throttledNotificationsUpdates$.subscribe();

    /**
     * Handle socket balance update events.
     */
    const handleSocketNotificationsUpdate = () => {
      notificationsUpdateSubject.next(true);
    };

    /**
     * Subscribe to the socket balance update event.
     */
    socket?.on('notificationsUpdate', handleSocketNotificationsUpdate);

    notificationsUpdateSubject.next(false);

    /**
     * Set interval
     */
    if (!refetchIntervalRef.current) {
      refetchIntervalRef.current = setInterval(() => {
        notificationsUpdateSubject.next(true);
      }, 60000); // Refetch every 60 seconds
    }

    return () => {
      subscription.unsubscribe(); // Unsubscribe from the RxJS stream
      socket?.off('notificationsUpdate', handleSocketNotificationsUpdate); // Remove the socket event listener
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current); // Clear the interval
        refetchIntervalRef.current = null; // Reset the refetch interval reference
      }
    };
  }, [fetchNotifications, socket]);

  // =={ Render }==============================================================
  /**
   * Render the notifications popover
   */
  return (
    <>
      <IconButtonAnimate
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          ...(open && { bgcolor: 'action.selected' }),
        }}
      >
        <Badge badgeContent={allCount} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75, display: 'flex', flexDirection: 'column' }}
        disabledArrow
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{t('title')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('description', { count: +allCount })}
            </Typography>
          </Box>

          {(category === '' || category === 'unread') && allCount > 0 && lockedCount !== allCount && (
            <Tooltip title={t('markAllAsRead')}>
              <IconButton color="inherit" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" color="success.main" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Tabs
          value={category}
          onChange={handleChangeCategory}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          aria-label="scrollable force tabs example"
        >
          <Tab
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  flexDirection: 'row',
                  gap: 1,
                }}
              >
                <Typography variant="inherit">{t('category.all')}</Typography>
                <Chip
                  label={Math.min(allCount, 99)}
                  color="default"
                  size="small"
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    minWidth: 25,
                    '& span': {
                      p: 0,
                    },
                  }}
                />
              </Box>
            }
            value=""
          />
          <Tab
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  flexDirection: 'row',
                  gap: 1,
                }}
              >
                <Typography variant="inherit">{t('category.unread')}</Typography>
              </Box>
            }
            value="unread"
          />
          {categories.map((option) => (
            <Tab
              key={option}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    flexDirection: 'row',
                    gap: 1,
                  }}
                >
                  <Typography variant="inherit">{t(`category.${option}`)}</Typography>
                  <Chip
                    label={Math.min(option in unreadCounts ? unreadCounts[option] : 0, 99)}
                    color="default"
                    size="small"
                    sx={{
                      px: 0.5,
                      py: 0.25,
                      minWidth: 25,
                      '& span': {
                        p: 0,
                      },
                    }}
                  />
                </Box>
              }
              value={option}
            />
          ))}
        </Tabs>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {notifications.length === 0 && !error && !loading && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('noNotifications')}
            </Typography>
          </Box>
        )}

        {!error && loading && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}

        {!!error && !loading && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {t('error')}
            </Typography>
            <Button
              onClick={() => {
                fetchNotifications();
              }}
              size="small"
              variant="outlined"
              color="warning"
              sx={{
                p: 0,
                px: 0.5,
              }}
            >
              {t('retry')}
            </Button>
          </Box>
        )}

        {!error && !loading && notifications.length > 0 && (
          <Scrollbar>
            <List disablePadding>
              {notifications.slice(0, 30).map((notification) => (
                <NotificationItem
                  key={notification.id + String(Math.random())}
                  notification={notification}
                  readHandler={handleMarkAsRead}
                  closeHandler={handleClose}
                />
              ))}
            </List>
          </Scrollbar>
        )}

        {/* <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple>
            {t('viewAll')}
          </Button>
        </Box> */}
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------
type NotificationItemProps = {
  notification: Notification;
  readHandler: (notificationId: string) => void;
  closeHandler: () => void;
};

function NotificationItem({ notification, readHandler, closeHandler }: NotificationItemProps) {
  // =={ Redux }==============================================================
  const interfacePreferences = useSelector((state) => state.auth.preferences);

  // =={ State }==============================================================
  const [dateNow, setDateNow] = React.useState(new Date());

  // =={ Refs }==============================================================
  const updateIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // =={ Hooks }==============================================================
  const { t } = useTranslation('notifications');

  // =={ Memos }==============================================================
  /**
   * Generate URL based on notification category and template ID
   */
  const url = React.useMemo(() => {
    let href = '';

    switch (notification.category) {
      case 'trading': {
        href = `/dashboard/sessions/${notification.templateData?.sessionId}`;
        break;
      }
      case 'wallet': {
        href = `/dashboard/wallet/orders/${notification.templateData?.orderId}`;
        break;
      }
      case 'security': {
        if (notification.templateId === 'account_restored') {
          href = ``;
        } else if (notification.templateId === 'suspicious_activity') {
          href = `/dashboard/manager/security/activity/login`;
        } else {
          href = `/dashboard/manager/security`;
        }
        break;
      }
      case 'affiliates': {
        href = `/dashboard/affiliates`;
        break;
      }
      default:
        break;
    }

    return href || null;
  }, [notification]);

  /**
   * Check if the notification is locked
   */
  const isLocked = React.useMemo(
    () => notification.lockedUntil && new Date(notification.lockedUntil) > new Date(),
    [notification]
  );

  /**
   *
   */
  const notificationDate = React.useMemo(() => {
    /**
     * if the notification createdAt is less than 24 hours ago, return the time in from now format
     * otherwise, return the date in a readable user format
     */
    const createdAt = dayjs(notification.createdAt);
    const now = dayjs(dateNow);
    if (now.diff(createdAt, 'hour') < 24) {
      return createdAt.fromNow();
    }
    return createdAt.format(
      `${interfacePreferences.dateFormat} ${interfacePreferences.timeFormat}`
    );
  }, [
    dateNow,
    notification.createdAt,
    interfacePreferences.dateFormat,
    interfacePreferences.timeFormat,
  ]);
  // =={ Handlers }==============================================================
  /**
   * Handle click on the notification item
   */
  const handleClick = () => {
    closeHandler();
    if (notification.readAt) return;
    if (isLocked) return;
    readHandler?.(notification.id);
  };

  // =={ Effects }==============================================================
  /**
   * Set up an interval to update the date now every minute
   */
  React.useEffect(() => {
    // Check if component is mounted
    if (updateIntervalRef.current) return;
    updateIntervalRef.current = setInterval(() => {
      setDateNow(new Date());
    }, 3000); // Update every 3 seconds
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, []);

  // =={ Render }==============================================================
  return (
    <ListItemButton
      {...(!!url
        ? {
            component: NextLink,
            href: url,
            passHref: true,
          }
        : {})}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
      }}
      onClick={handleClick}
    >
      <ListItemAvatar>
        <Avatar sx={{ padding: 0.5, backgroundColor: 'unset' }}>
          {notification.category === 'wallet' && (
            <Iconify icon="solar:wallet-bold" sx={{ width: '100%', height: '100%' }} />
          )}
          {notification.category === 'security' && (
            <Iconify icon="grommet-icons:secure" sx={{ width: '100%', height: '100%' }} />
          )}
          {notification.category === 'trading' && (
            <Iconify
              icon="streamline-ultimate:trading-pattern-up-bold"
              sx={{ width: '100%', height: '100%' }}
            />
          )}
          {notification.category === 'affiliates' && (
            <Iconify icon="mdi:partnership-outline" sx={{ width: '100%', height: '100%' }} />
          )}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box
            sx={{
              position: 'relative',
            }}
          >
            {!Boolean(notification.readAt) && !isLocked && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 0,
                  borderRadius: '50%',
                  width: 8,
                  height: 8,
                  bgcolor: 'info.main',
                }}
              />
            )}
            {isLocked && (
              <Iconify
                icon="bxs:lock"
                color="error.main"
                sx={{ position: 'absolute', top: 4, right: 0 }}
              />
            )}
            <Typography
              variant="body2"
              fontWeight={!Boolean(notification.readAt) || isLocked ? 'bold' : 'normal'}
            >
              {t(`templates.${notification.category}.${notification.templateId}.title`)}&nbsp;
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <Trans
                i18nKey={`templates.${notification.category}.${notification.templateId}.desc`}
                t={t}
                values={{
                  ...(notification.templateData || {}),
                  /**
                   * When the templateData contains a reason, we translate it
                   * based on the templateId and category.
                   */
                  ...(notification.templateData?.reason &&
                    notification.templateId === 'trading_session_renew_error' && {
                      reason: t(
                        `templates.${notification.category}.${notification.templateId}.reasons.${notification.templateData.reason}`
                      ),
                    }),
                  /**
                   * When the templateData contains a method, we translate it
                   */
                  ...(notification.templateData?.method &&
                    notification.category === 'wallet' && {
                      method: t(
                        `templates.${notification.category}.methods.${notification.templateData.method}`
                      ),
                    }),
                }}
              >
                {typeof notification.templateData?.amount !== 'undefined' ? (
                  <NumericFormat
                    dir="ltr"
                    displayType="text"
                    suffix=" USDT"
                    value={+notification.templateData.amount || 0}
                    thousandSeparator={separators[interfacePreferences.thousandSeparator]}
                    decimalSeparator={separators[interfacePreferences.decimalSeparator]}
                    decimalScale={2}
                  />
                ) : (
                  <></>
                )}
                {typeof notification.templateData?.pnl !== 'undefined' ? (
                  <NumericFormat
                    dir="ltr"
                    displayType="text"
                    suffix=" USDT"
                    value={+notification.templateData.pnl || 0}
                    thousandSeparator={separators[interfacePreferences.thousandSeparator]}
                    decimalSeparator={separators[interfacePreferences.decimalSeparator]}
                    decimalScale={2}
                    prefix={+notification.templateData.pnl > 0 ? '+' : ''}
                  />
                ) : (
                  <></>
                )}
                {typeof notification.templateData?.startMargin !== 'undefined' ? (
                  <NumericFormat
                    dir="ltr"
                    displayType="text"
                    suffix=" USDT"
                    value={+notification.templateData.startMargin || 0}
                    thousandSeparator={separators[interfacePreferences.thousandSeparator]}
                    decimalSeparator={separators[interfacePreferences.decimalSeparator]}
                    decimalScale={2}
                  />
                ) : (
                  <></>
                )}
              </Trans>
            </Typography>
          </Box>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {notificationDate}
          </Typography>
        }
      />
    </ListItemButton>
  );
}
